import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";

const Footer: React.FC = () => {
  const { t, dir } = useLanguage();
  const isRtl = dir === "rtl";

  const handleScrollTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const companyLinks = [
    //{ to: "/blog", label: t?.nav?.blog || "Blog" },
    { to: "/contact", label: t?.nav?.contact || "Contact Us" },
    { to: "/services", label: t?.nav?.services || "Services" },
    { to: "/about", label: t?.nav?.about || "Who We Are" },
  ];

  const footerContent = (t as any)?.footer || {};
  const companyLabel = footerContent.company || "Company";
  const contactLabel = footerContent.contactTitle || "Contact";
  const privacyLabel = footerContent.privacy || "Privacy Policy";
  const termsLabel = footerContent.terms || "Terms of Service";
  const rightsLabel =
    footerContent.rightsFull || "© 2026 Welcome. All right reserved.";

  const contactItems = [
    {
      value: "01240079406 / 01240079407",
      icon: (
        <path
          d="M7 4H10L11.5 8L9.5 9.5C10.3573 11.2387 11.7613 12.6427 13.5 13.5L15 11.5L19 13V16C19 16.5304 18.7893 17.0391 18.4142 17.4142C18.0391 17.7893 17.5304 18 17 18C13.2868 17.7741 9.79075 16.1971 7.17157 13.5784C4.55238 10.9592 2.97593 7.46317 2.75 3.75C2.75 3.21957 2.96071 2.71086 3.33579 2.33579C3.71086 1.96071 4.21957 1.75 4.75 1.75H8L7 4Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ),
    },
    {
      value: "info@cvis.com.eg",
      icon: (
        <>
          <path d="M4 6H20V18H4V6Z" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="M4 7L12 13L20 7"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      ),
    },
    {
      value: "Building No. 129, Tahrir Street, Dokki - Giza",
      icon: (
        <>
          <path
            d="M12 21C15.5 16.8 18 13.6 18 10C18 6.68629 15.3137 4 12 4C8.68629 4 6 6.68629 6 10C6 13.6 8.5 16.8 12 21Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5" />
        </>
      ),
    },
    {
      value: "Sunday – Thursday, 8:00 AM – 4:00 PM",
      icon: (
        <>
          <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="M12 7.5V12L15 13.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      ),
    },
  ];

  const socials = [
    {
      href: "https://facebook.com",
      label: "Facebook",
      icon: (
        <path
          d="M11.1667 17.5V10.8333H13.4167L13.75 8.33333H11.1667V6.75C11.1667 6.02667 11.3667 5.53333 12.4083 5.53333H13.8333V3.3C13.1417 3.225 12.445 3.18889 11.75 3.19167C9.69167 3.19167 8.33333 4.45 8.33333 6.75833V8.33333H6.08333V10.8333H8.33333V17.5H11.1667Z"
          fill="currentColor"
        />
      ),
    },
    {
      href: "https://instagram.com",
      label: "Instagram",
      icon: (
        <>
          <rect x="3" y="3" width="14" height="14" rx="4" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="10" cy="10" r="3.25" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="14.25" cy="5.75" r="0.9" fill="currentColor" />
        </>
      ),
    },
    {
      href: "https://linkedin.com",
      label: "LinkedIn",
      icon: (
        <>
          <path d="M5.16667 7.33333H2.66667V17.3333H5.16667V7.33333Z" fill="currentColor" />
          <path d="M3.91667 5.83333C4.83714 5.83333 5.58333 5.08714 5.58333 4.16667C5.58333 3.24619 4.83714 2.5 3.91667 2.5C2.99619 2.5 2.25 3.24619 2.25 4.16667C2.25 5.08714 2.99619 5.83333 3.91667 5.83333Z" fill="currentColor" />
          <path d="M17.75 11.1083C17.75 8.06667 15.8167 6.91667 13.975 6.91667C12.4875 6.91667 11.825 7.73333 11.45 8.30833V7.33333H8.95C8.98333 7.975 8.95 17.3333 8.95 17.3333H11.45V11.75C11.45 11.45 11.475 11.15 11.5583 10.9333C11.7833 10.3333 12.2917 9.70833 13.15 9.70833C14.275 9.70833 14.725 10.5667 14.725 11.825V17.3333H17.225V11.1083H17.75Z" fill="currentColor" />
        </>
      ),
    },
  ];

  return (
    <footer className="bg-second-color text-white">
      <div className="site-container flex flex-col gap-0 pt-14 sm:pt-18 lg:pt-24">

        {/* ── Main content row ── */}
        <div
          className={`flex flex-col gap-12 pb-12 lg:flex-row lg:items-start lg:justify-between lg:pb-16 ${
            isRtl ? "lg:flex-row-reverse" : ""
          }`}
        >
          {/* Logo + tagline */}
          <div
            className={`flex w-full flex-shrink-0 flex-col gap-5 lg:max-w-[220px] ${
              isRtl ? "items-end text-right" : "items-start text-left"
            }`}
          >
            <Link
              to="/"
              onClick={handleScrollTop}
              aria-label="Eduvate home"
              className="inline-flex items-center justify-center opacity-100 transition-opacity hover:opacity-80"
            >
            <img
              src="/images/kk.svg"
              alt="Eduvate"
              className="h-[80px] w-[160px]"
            />
            </Link>
            {/* Subtle decorative rule under logo */}
            <div className="h-px w-40 bg-white/20" />
          </div>

          {/* Links + Contact */}
          <div
            className={`grid w-full gap-10 sm:grid-cols-2 lg:max-w-[780px] lg:gap-16 ${
              isRtl ? "lg:text-right" : "lg:text-left"
            }`}
          >
            {/* Company links */}
            <div
              className={`flex flex-col gap-5 ${
                isRtl ? "items-end text-right" : "items-start text-left"
              }`}
            >
              <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                {companyLabel}
              </p>
              <div className="flex flex-col gap-3">
                {companyLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={handleScrollTop}
                    className={`group relative inline-flex items-center gap-2 font-sans text-sm font-normal leading-6 text-white/80 transition-colors hover:text-main-color sm:text-base ${
                      isRtl ? "flex-row-reverse" : ""
                    }`}
                  >
                    <span
                      className={`absolute h-1 w-1 scale-0 rounded-full bg-main-color transition-transform group-hover:scale-100 ${
                        isRtl ? "-right-3" : "-left-3"
                      }`}
                    />
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Contact info */}
            <div
              className={`flex flex-col gap-5 ${
                isRtl ? "items-end text-right" : "items-start text-left"
              }`}
            >
              <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                {contactLabel}
              </p>
              <div className="flex flex-col gap-4">
                {contactItems.map((item, i) => (
                  <div
                    key={i}
                    className={`flex items-start gap-3 ${
                      isRtl ? "flex-row-reverse text-right items-end" : "items-start"
                    }`}
                  >
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center text-white/50">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        {item.icon}
                      </svg>
                    </span>
                    <p className="font-sans text-sm font-normal leading-6 text-white/80">
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Bottom bar ── */}
        <div className="border-t border-white/10">
          <div
            className={`flex flex-col gap-4 py-5 sm:flex-row sm:items-center sm:justify-between sm:gap-0 ${
              isRtl ? "sm:flex-row-reverse" : ""
            }`}
          >
            {/* Legal links */}
            <div
              className={`flex flex-wrap items-center gap-x-5 gap-y-1.5 font-sans text-xs text-white/40 ${
                isRtl ? "justify-end text-right" : "justify-start text-left"
              }`}
            >
              <p>{rightsLabel}</p>
              <span className="hidden text-white/20 sm:inline">·</span>
              <Link to="/" onClick={handleScrollTop} className="transition-colors hover:text-main-color">
                {privacyLabel}
              </Link>
              <span className="hidden text-white/20 sm:inline">·</span>
              <Link to="/" onClick={handleScrollTop} className="transition-colors hover:text-main-color">
                {termsLabel}
              </Link>
            </div>

            {/* Social icons */}
            <div
              className={`flex items-center gap-3 ${
                isRtl ? "flex-row-reverse" : ""
              }`}
            >
              {socials.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={social.label}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-white/50 transition-all hover:border-main-color/40 hover:bg-main-color/10 hover:text-main-color"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    {social.icon}
                  </svg>
                </a>
              ))}
            </div>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;
